import React, { useEffect } from "react";
import { makeStyles, withStyles } from "@material-ui/core/styles";
import Modal from "@material-ui/core/Modal";

import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import CloseIcon from "../../icons/close.svg";
import ForwardIcon from "@mui/icons-material/Forward";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import CancelIcon from "@mui/icons-material/Cancel";

import {
  Box,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Button,
  Tooltip,
} from "@material-ui/core";
import { formatDateString } from "../../helpers/date";
import {
  ORDER_ITEM_STATUS_BY_ID,
  ORDER_ITEM_STATUS_CANCELLED,
  ORDER_ITEM_STATUS_RECEIVED,
  ORDER_PAYMENT_BY_ID,
  ORDER_SHIPPING_BY_ID,
  ORDER_STATE_BY_ID,
  ORDER_STATE_CONFIRMED,
  ORDER_STATE_CREATED,
} from "../../constants/order";
import { associateShipmentsWithItems } from "../../helpers/orderHelper";
import { isAdminUser } from "../../helpers/user-session";
import { currencyFormat, formatterPEN } from "../../helpers/moneyFormat";
import { useOrderHandler } from "../../hooks/rest";
import SelectWarehouseModal from "./SelectWarehouseModal";
import { useInventoryHandler } from "../../hooks/rest/use-inventory-handler";
import ProcessOrderDialog from "./ProcessOrderDialog";
import CancelOrderItemDialog from "./CancelOrderItemDialog";
import { IOrderShipment } from "../../interfaces/order/order.shipment.interface";
import { IOrderShipmentAddress } from "../../interfaces/order/order.shipment.address.interface";
import * as EntitlementHelper from "../../helpers/entitlementHelper";
import ProcessOrderRetry from "./ProcessOrderRetry";

const StyledTableCell = withStyles((theme) => ({
  head: {
    backgroundColor: "#E0E0E0",
    color: theme.palette.common.black,
    fontWeight: "bold",
  },
  body: {
    fontSize: 14,
    border: "solid 1px black",
  },
}))(TableCell);

const useStyles = makeStyles((theme) => ({
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  paper: {
    backgroundColor: theme.palette.background.paper,
    border: "1px solid #eee5e5",
    width: "calc(60vw + 200px)",
    borderRadius: "5px",
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2, 4, 3),
    "@media (max-width: 1280px)": {
      maxHeight: "calc(100vh - 50px)",
    },
    "@media (max-width: 1360px)": {
      maxHeight: "calc(100vh - 80px)",
    },
  },
  tableContainer: {
    maxHeight: 400,
    overflowY: "auto",
    "@media (max-width: 1280px)": {
      maxHeight: 250,
    },
    "@media (min-width: 1281px) and (max-width: 1360px)": {
      maxHeight: 280,
    },
  },
  label: {
    marginBottom: "10px",
  },
}));

export default function OrderDetailsModal({
  isOpen,
  setOpen,
  order,
  children,
  setRefetchOrders,
  timezoneName,
  setShowLoader,
}: any) {
  const classes = useStyles();

  const { getOrdersById } = useOrderHandler();
  const { getInventory } = useInventoryHandler();
  const [orderDetail, setOrderDetail] = React.useState(order);

  const [orderItems, setOrderItems] = React.useState();
  const [shipmentAddresses, setShipmentAddresses] = React.useState<IOrderShipmentAddress[]>([]);
  const [openProcessModal, setOpenProcessModal] = React.useState(false);
  const [itemToProcess, setItemToProcess] = React.useState();
  const [itemToCancel, setItemToCancel] = React.useState();
  const [openCancelDialog, setOpenCancelDialog] = React.useState(false);

  // Retry place order
  const [orderToProcess, setOrderToProcess] = React.useState();
  const [openRetryModal, setOpenRetryModal] = React.useState(false);

  // Warehouse handle
  const [openWarehouseDialog, setOpenWarehouseDialog] = React.useState(false);
  const [warehouses, setWarehouses] = React.useState();
  const [itemWarehouse, setItemWarehouse] = React.useState();

  const handleClose = () => {
    setOpen(false);
  };

  const updateOrderDetails = (orderId = null) => {
    const [respOrdersById] = getOrdersById(orderId || orderDetail.orderId);
    respOrdersById.then((resp) => {
      if (resp?.data?.data?.status === 2) {
        setRefetchOrders(true);
      }
      setOrderDetail(resp.data.data);
    });
  };

  useEffect(() => {
    setOrderDetail(order);
  }, [order]);

  useEffect(() => {
    if (orderDetail) {
      const mapOrderItems = associateShipmentsWithItems(orderDetail);
      setOrderItems(mapOrderItems);
      groupShippingAddress(orderDetail.orderShipments);
    }
  }, [orderDetail]);

  const handleProcessItem = (orderItem: any) => {
    setOpenProcessModal(true);
    setItemToProcess(orderItem);
  };

  const handleCancelItem = (orderItem: any) => {
    setOpenCancelDialog(true);
    setItemToCancel(orderItem);
  };

  const handleWarehouse = (order: any, orderItem: any) => {
    setItemWarehouse(orderItem);
    setShowLoader(true);
    const [respFfmcentersInventoryBySku] = getInventory(
      order.store.storeId,
      1000,
      undefined,
      orderItem.sku,
      "quantity",
      "false"
    );
    respFfmcentersInventoryBySku
      .then((resp) => {
        const ffmCenters = resp.data.data.map((record: any) => {
          return {
            fulfillmentCenterId: record.ffmCenterId,
            name: record.ffmCenterName,
            identifier: record.sucursal,
            inventory: {
              sku: record.sku,
              quantity: record.quantity,
            },
          };
        });
        setWarehouses(ffmCenters);
      })
      .finally(() => {
        setOpenWarehouseDialog(true);
        setShowLoader(false);
      });
  };

  const hasExternalShipmentNo = (order: any): boolean => {
    if (orderDetail.status !== ORDER_STATE_CONFIRMED) {
      return false;
    }

    const orderShipments = order.orderShipments;

    return orderShipments.filter((os: any) => os.externalShipmentNo).length > 0;
  };

  const hasTrackingCode = (order: any): boolean => {
    if (orderDetail.status !== ORDER_STATE_CONFIRMED) {
      return false;
    }

    const orderShipments = order.orderShipments;

    return orderShipments.filter((os: any) => os.trackingCode).length > 0;
  };

  const getTrackingCode = (order: any, orderItem: any): string => {
    let trackingCode = "";
    if (orderItem.orderItemStatus === ORDER_ITEM_STATUS_CANCELLED) {
      return trackingCode;
    }

    const orderShipments = order.orderShipments;
    const shipmentLineNo = orderItem.shipmentLineNo;

    const orderShipment = orderShipments.find((os: any) => os.shipmentLineNo === shipmentLineNo);
    if (orderShipment) {
      trackingCode = orderShipment.trackingCode;
    }

    return trackingCode;
  };

  const getExternalShipmentNo = (order: any, orderItem: any): string => {
    let externalShipmentNo = "";
    if (orderItem.orderItemStatus === ORDER_ITEM_STATUS_CANCELLED) {
      return externalShipmentNo;
    }

    const orderShipments = order.orderShipments;
    const shipmentLineNo = orderItem.shipmentLineNo;

    const orderShipment = orderShipments.find((os: any) => os.shipmentLineNo === shipmentLineNo);
    if (orderShipment) {
      externalShipmentNo = orderShipment.externalShipmentNo;
    }

    return externalShipmentNo;
  };

  const hasPickupCode = (order: any): boolean => {
    if (orderDetail.status !== ORDER_STATE_CONFIRMED) {
      return false;
    }

    const orderShipments = order.orderShipments;

    return orderShipments.filter((os: any) => os.pickupCode).length > 0;
  };

  const getPickupCode = (order: any, orderItem: any): string => {
    let pickupCode = "";
    if (orderItem.orderItemStatus === ORDER_ITEM_STATUS_CANCELLED) {
      return pickupCode;
    }

    const orderShipments = order.orderShipments;
    const shipmentLineNo = orderItem.shipmentLineNo;

    const orderShipment = orderShipments.find((os: any) => os.shipmentLineNo === shipmentLineNo);
    if (orderShipment) {
      pickupCode = orderShipment.pickupCode;
    }

    return pickupCode;
  };

  const hasCourierIdentifier = (order: any): boolean => {
    if (orderDetail.status !== ORDER_STATE_CONFIRMED) {
      return false;
    }

    const orderShipments = order.orderShipments;

    return orderShipments.filter((os: any) => os.courier).length > 0;
  };

  const getCourierIdentifier = (order: any, orderItem: any): string => {
    let courierIdentifier = "";
    if (orderItem.orderItemStatus === ORDER_ITEM_STATUS_CANCELLED) {
      return courierIdentifier;
    }

    const orderShipments = order.orderShipments;
    const shipmentLineNo = orderItem.shipmentLineNo;

    const orderShipment = orderShipments.find((os: any) => os.shipmentLineNo === shipmentLineNo);
    if (orderShipment) {
      courierIdentifier = orderShipment.courier;
    }

    return courierIdentifier;
  };

  const groupShippingAddress = (orderShipments: IOrderShipment[]) => {
    const addresses: IOrderShipmentAddress[] = [];

    const compareAddresses = (addressA: IOrderShipmentAddress, addressB: IOrderShipmentAddress): boolean => {
      return (
        addressA.contactName === addressB.contactName &&
        addressA.contactLastname === addressB.contactLastname &&
        addressA.contactEmail === addressB.contactEmail &&
        addressA.contactPhone === addressB.contactPhone &&
        addressA.address === addressB.address &&
        addressA.externalNumber === addressB.externalNumber &&
        addressA.internalNumber === addressB.internalNumber &&
        addressA.zipCode === addressB.zipCode &&
        addressA.countryCode === addressB.countryCode &&
        addressA.countryName === addressB.countryName &&
        addressA.stateName === addressB.stateName &&
        addressA.cityName === addressB.cityName &&
        addressA.settlementName === addressB.settlementName
      );
    };

    orderShipments.forEach((orderShipment: IOrderShipment) => {
      if (!orderShipment.contactName) {
        return;
      }

      const shipmentAddress: IOrderShipmentAddress = {
        contactName: orderShipment.contactName,
        contactLastname: orderShipment.contactLastname,
        contactEmail: orderShipment.contactEmail,
        contactPhone: orderShipment.contactPhone,
        address: orderShipment.address,
        externalNumber: orderShipment.externalNumber,
        internalNumber: orderShipment.internalNumber,
        zipCode: orderShipment.zipCode,
        countryCode: orderShipment.countryCode,
        countryName: orderShipment.countryName,
        stateName: orderShipment.stateName,
        cityName: orderShipment.cityName,
        settlementName: orderShipment.settlementName,
      };

      const foundAddress: IOrderShipmentAddress | undefined = addresses.find((address: IOrderShipmentAddress) =>
        compareAddresses(address, shipmentAddress)
      );
      if (!foundAddress) {
        addresses.push(shipmentAddress);
      }
    });
    setShipmentAddresses(addresses);
  };

  const renderShipmentAddress = (): any => {
    if (shipmentAddresses.length > 0) {
      const shipmentAddressesRXJS = shipmentAddresses.map((shipmentAddress: IOrderShipmentAddress) => (
        <>
          <hr></hr>
          <Typography variant="h3" style={{ margin: "5px 0 13px 0", fontWeight: "bold" }}>
            Dirección de envío:
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <Typography variant="body1" className={classes.label}>
                <b>Contacto: </b>
                {`${shipmentAddress.contactName} ${shipmentAddress.contactLastname}`.trim()}
              </Typography>
              <Typography variant="body1" className={classes.label}>
                <b>Email: </b>
                {shipmentAddress.contactEmail}
              </Typography>
              <Typography variant="body1" className={classes.label}>
                <b>Teléfono: </b>
                {shipmentAddress.contactPhone}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="body1" className={classes.label}>
                <b>Dirección: </b>
                {shipmentAddress.address}
              </Typography>
              <Typography variant="body1" className={classes.label}>
                <b>Número exterior: </b>
                {shipmentAddress.externalNumber}
              </Typography>
              <Typography variant="body1" className={classes.label}>
                <b>Número interior: </b>
                {shipmentAddress.internalNumber}
              </Typography>
              <Typography variant="body1" className={classes.label}>
                <b>Código postal: </b>
                {shipmentAddress.zipCode}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="body1" className={classes.label}>
                <b>País: </b>
                {shipmentAddress.countryName}
              </Typography>
              <Typography variant="body1" className={classes.label}>
                <b>Estado: </b>
                {shipmentAddress.stateName}
              </Typography>
              <Typography variant="body1" className={classes.label}>
                <b>Ciudad: </b>
                {shipmentAddress.cityName}
              </Typography>
              <Typography variant="body1" className={classes.label}>
                <b>Municipio: </b>
                {shipmentAddress.settlementName}
              </Typography>
            </Grid>
          </Grid>
        </>
      ));
      return shipmentAddressesRXJS;
    }
    return <></>;
  };

  const handlePlaceOrderRetry = () => {
    setOpenRetryModal(true);
    setOrderToProcess(orderDetail);
  };

  const renderRetryButton = (): any => {
    if (
      EntitlementHelper.hasPlaceOrderRetryAccess() &&
      orderDetail &&
      orderItems &&
      orderDetail.status === ORDER_STATE_CREATED
    ) {
      const pendingOrderItems = orderItems.filter((oi: any) => oi.orderItemStatus === ORDER_ITEM_STATUS_RECEIVED);
      if (pendingOrderItems.length === 0) {
        return (
          <>
            <hr></hr>
            <Grid container spacing={2}>
              <Grid item xs={12} style={{ textAlign: "right" }}>
                <Button onClick={handlePlaceOrderRetry} variant="contained" color="primary">
                  Reintentar
                </Button>
              </Grid>
            </Grid>
          </>
        );
      }
    }
    return <></>;
  };

  const handleDownloadLabels = () => {
    setShowLoader(true);

    try {
      const orderShipments = orderDetail.orderShipments.filter((os: any) => os.trackingLabelUrl);

      for (let i = 0; i < orderShipments.length; i++) {
        let orderShipment = orderShipments[i];
        let trackingLabel = orderShipment.trackingLabelUrl;

        fetch(trackingLabel)
          .then((response) => response.blob())
          .then((blob) => {
            var url = window.URL.createObjectURL(blob);
            var a = document.createElement("a");
            a.href = url;
            a.type = "application/octet-stream";
            a.download = `${orderShipment.externalShipmentNo}`;
            document.body.appendChild(a);
            a.click();
            a.remove();
          });
      }
    } catch (error) {
      console.error("Error al descargar las etiquetas");
      console.error(error);
    } finally {
      setShowLoader(false);
    }
  };

  const renderDownloadLabelsButton = (): any => {
    if (!orderDetail || !orderItems) {
      return <></>;
    }

    const orderShipments = orderDetail.orderShipments;

    const hasTrackingLabel = orderShipments.filter((os: any) => os.trackingLabelUrl).length > 0;
    if (!hasTrackingLabel) {
      return <></>;
    }

    return (
      <>
        <hr></hr>
        <Grid container spacing={2}>
          <Grid item xs={12} style={{ textAlign: "right" }}>
            <Button onClick={handleDownloadLabels} variant="contained" color="primary">
              Descargar etiquetas
            </Button>
          </Grid>
        </Grid>
      </>
    );
  };

  return (
    <div>
      <Modal
        className={classes.modal}
        open={isOpen}
        onClose={handleClose}
        aria-labelledby="simple-modal-title"
        aria-describedby="simple-modal-description">
        <div className={classes.paper}>
          <Grid
            style={{
              justifyContent: "space-between",
              marginBottom: "-50px",
              marginTop: "10px",
            }}
            xs={12}
            container
            direction="row"
            alignItems="flex-start">
            <Typography variant="h2"></Typography>
            <Typography variant="h3">
              <img src={CloseIcon} alt="Icon" style={{ cursor: "pointer" }} onClick={handleClose} />
            </Typography>
          </Grid>
          {orderDetail && (
            <Box p={2}>
              <Box p={2} mb={2}>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <Typography variant="body1" className={classes.label}>
                      <b>Orden</b>: {orderDetail.orderNo}
                    </Typography>
                    <Typography variant="body1" className={classes.label}>
                      <b>Fecha</b>: {formatDateString(timezoneName, orderDetail.orderDate)}
                    </Typography>
                    {orderDetail.customerName && (
                      <Typography variant="body1" className={classes.label}>
                        <b>Nombre Cliente</b>: {orderDetail.customerName} {orderDetail.customerLastname}
                      </Typography>
                    )}
                    {orderDetail.customerEmail && (
                      <Typography variant="body1" className={classes.label}>
                        <b>Email Cliente</b>: {orderDetail.customerEmail}
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="body1" className={classes.label}>
                      <b>Estado</b>: {ORDER_STATE_BY_ID[orderDetail.status]}
                    </Typography>
                    <Typography variant="body1" className={classes.label}>
                      <b>Estado de pago</b>: {ORDER_PAYMENT_BY_ID[orderDetail.paymentStatus].name}
                    </Typography>
                    <Typography variant="body1" className={classes.label}>
                      <b>Estado de Envío</b>: {ORDER_SHIPPING_BY_ID[orderDetail.shippingStatus].name}
                    </Typography>
                    {orderDetail.orderPayments && orderDetail.orderPayments.length > 0 && (
                      <Typography variant="body1" className={classes.label}>
                        <b>Método de pago</b>: {orderDetail.orderPayments[0].paymentMethod}
                      </Typography>
                    )}
                  </Grid>
                  {orderDetail.totalAmount && (
                    <Grid item xs={4}>
                      <Typography variant="body1" className={classes.label}>
                        <b>Subtotal</b>: {currencyFormat(orderDetail.currency, orderDetail.subtotalAmount)}
                      </Typography>
                      <Typography variant="body1" className={classes.label}>
                        <b>Descuentos</b>: {currencyFormat(orderDetail.currency, orderDetail.discountAmount)}
                      </Typography>
                      <Typography variant="body1" className={classes.label}>
                        <b>Envío</b>: {currencyFormat(orderDetail.currency, orderDetail.shippingAmount)}
                      </Typography>
                      <Typography variant="body1" className={classes.label}>
                        <b>Total</b>: {currencyFormat(orderDetail.currency, orderDetail.totalAmount)}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
                {renderShipmentAddress()}
                {renderRetryButton()}
                {renderDownloadLabelsButton()}
              </Box>
              <TableContainer component={Paper} className={classes.tableContainer}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <StyledTableCell>SKU</StyledTableCell>
                      <StyledTableCell>Producto</StyledTableCell>
                      <StyledTableCell>Cantidad</StyledTableCell>
                      <StyledTableCell>Estado</StyledTableCell>
                      <StyledTableCell>Bodega</StyledTableCell>
                      {hasCourierIdentifier(orderDetail) && <StyledTableCell>Courier</StyledTableCell>}
                      {hasTrackingCode(orderDetail) && <StyledTableCell>Envío</StyledTableCell>}
                      {hasPickupCode(orderDetail) && <StyledTableCell>Recojo</StyledTableCell>}
                      <StyledTableCell align="right"></StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {orderItems &&
                      orderItems.map((item: any) => (
                        <TableRow>
                          <TableCell>{item.sku}</TableCell>
                          <TableCell>{item.productName}</TableCell>
                          <TableCell>{item.quantity}</TableCell>
                          <TableCell>{ORDER_ITEM_STATUS_BY_ID[item.orderItemStatus].name}</TableCell>
                          <TableCell>{item.orderShipment ? item.orderShipment.fulfillmentCenter.name : ""}</TableCell>
                          {hasCourierIdentifier(orderDetail) && (
                            <TableCell>{getCourierIdentifier(orderDetail, item)}</TableCell>
                          )}
                          {hasTrackingCode(orderDetail) && (
                            <TableCell>{getTrackingCode(orderDetail, item)}</TableCell>
                          )}
                          {hasPickupCode(orderDetail) && <TableCell>{getPickupCode(orderDetail, item)}</TableCell>}
                          <TableCell align="center">
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "center",
                                gap: "4px",
                              }}>
                              {ORDER_ITEM_STATUS_BY_ID[item.orderItemStatus].id === 0 && (
                                <>
                                  {EntitlementHelper.hasPlaceOrderItemAccess() && (
                                    <Tooltip title="Procesar" placement="top">
                                      <Button
                                        type="button"
                                        variant="text"
                                        onClick={() => handleProcessItem(item)}
                                        style={{
                                          minWidth: "auto",
                                          padding: "4px",
                                        }}>
                                        <ForwardIcon />
                                      </Button>
                                    </Tooltip>
                                  )}
                                  {EntitlementHelper.hasUpdateOrderItemAccess() && (
                                    <Tooltip title="Cambiar Bodega" placement="top">
                                      <Button
                                        type="button"
                                        variant="text"
                                        onClick={() => handleWarehouse(orderDetail, item)}
                                        style={{
                                          minWidth: "auto",
                                          padding: "4px",
                                        }}>
                                        <WarehouseIcon />
                                      </Button>
                                    </Tooltip>
                                  )}
                                </>
                              )}
                              {[0, 1].includes(ORDER_ITEM_STATUS_BY_ID[item.orderItemStatus].id) && (
                                <>
                                  {EntitlementHelper.hasCancelOrderItemAccess() && (
                                    <Tooltip title="Cancelar" placement="top">
                                      <Button
                                        type="button"
                                        variant="text"
                                        onClick={() => handleCancelItem(item)}
                                        style={{
                                          minWidth: "auto",
                                          padding: "4px",
                                        }}>
                                        <CancelIcon />
                                      </Button>
                                    </Tooltip>
                                  )}
                                </>
                              )}
                            </div>
                            {/* Modal process item*/}
                            {itemToProcess && (
                              <ProcessOrderDialog
                                itemToProcess={itemToProcess}
                                openProcessModal={openProcessModal}
                                setOpenProcessModal={setOpenProcessModal}
                                updateOrderDetails={updateOrderDetails}
                              />
                            )}
                            {orderToProcess && (
                              <ProcessOrderRetry
                                orderToProcess={orderToProcess}
                                openProcessModal={openRetryModal}
                                setOpenProcessModal={setOpenRetryModal}
                                updateOrderDetails={updateOrderDetails}
                              />
                            )}
                            {/* Modal para cambiar la bodega */}
                            <SelectWarehouseModal
                              isOpen={openWarehouseDialog}
                              setOpen={setOpenWarehouseDialog}
                              order={orderDetail}
                              warehouses={warehouses}
                              itemWarehouse={itemWarehouse}
                              updateOrderDetails={updateOrderDetails}
                            />
                            {/* Modal cancel item */}
                            <CancelOrderItemDialog
                              order={orderDetail}
                              itemToCancel={itemToCancel}
                              openCancelModal={openCancelDialog}
                              setOpenCancelModal={setOpenCancelDialog}
                              updateOrderDetails={updateOrderDetails}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </div>
      </Modal>
    </div>
  );
}
